import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import {
  getClientDataById,
  listClientsByParent
} from '../../../../services/clients'
import { clientContext } from '../../../../contexts/ClientContext'

const AssignDocumentsDialog = ({
  open,
  handleClose,
  onContinue,
  subcategories,
  clientId,
  selectedClientOrCompanyId,
  selectedClientOrCompanyName,
  selectedWorkspaceId,
  selectedSubcategoryId,
  selectedCategoryName,
  selectedFileId,
  selectedFileName
}) => {
  const { data } = useContext(clientContext)
  const [files, setFiles] = useState([])
  const fileInputRef = useRef(null)
  const [clientOrCompanies, setClientOrCompanies] = useState([])
  const [selectedClientOrCompany, setSelectedClientOrCompany] = useState(
    selectedClientOrCompanyId
  )
  const [selectedWorkspace, setSelectedWorkspace] =
    useState(selectedWorkspaceId)
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    selectedSubcategoryId
  )
  const [selectedFile, setSelectedFile] = useState(selectedFileId)

  const fetchClientOrCompanies = async (clientId) => {
    const clientData = await getClientDataById(clientId)
    clientData.type = 'client'
    const companies = await listClientsByParent(clientId)
    setClientOrCompanies([clientData, ...companies])
  }

  useEffect(() => {
    if (!open) {
      setSelectedClientOrCompany(null)
      setSelectedWorkspace(null)
      setSelectedSubcategory(null)
      setSelectedFile(null)
      setFiles([])
    } else {
      if (clientOrCompanies.length === 0) {
        fetchClientOrCompanies(clientId)
      }
      setSelectedClientOrCompany(selectedClientOrCompanyId)
      setSelectedWorkspace(selectedWorkspaceId)
      setSelectedSubcategory(selectedSubcategoryId)
      setSelectedFile(selectedFileId)
    }
  }, [open])

  const availableCustomDirs = useMemo(() => {
    if (
      !selectedClientOrCompany ||
      !selectedWorkspace ||
      !selectedSubcategory ||
      !data
    )
      return []

    const clientOrCompany =
      data.id === selectedClientOrCompany
        ? data
        : data.companies?.find((c) => c.id === selectedClientOrCompany)

    if (!clientOrCompany) return []

    const workspace = clientOrCompany.workspaces?.find(
      (w) => w.workspace_id === selectedWorkspace
    )
    if (!workspace) return []

    const subcategory = workspace.subcategories?.find(
      (s) => s.id === selectedSubcategory
    )
    if (!subcategory) return []

    return subcategory.files ?? []
  }, [selectedClientOrCompany, selectedWorkspace, selectedSubcategory, data])

  const handleFiles = (newFiles) => {
    setFiles((prev) => [...prev, ...Array.from(newFiles)])
  }

  const onDrop = (e) => {
    e.preventDefault()
    if (e.dataTransfer.files) handleFiles(e.dataTransfer.files)
  }

  const renderSelectGroup = (workspace, idxWorkspace) => {
    const items = workspace.subcategories.map((subcategory, idxSubcategory) => (
      <MenuItem
        key={idxWorkspace * 1000 + idxSubcategory}
        value={idxWorkspace * 1000 + idxSubcategory}
      >
        {window.$lang === 'en'
          ? subcategory.subcategory_en
          : subcategory.subcategory_es || subcategory.subcategory_en}
      </MenuItem>
    ))
    return [
      <ListSubheader key={`header-${idxWorkspace}`}>
        {window.$lang === 'en'
          ? workspace.workspace.name_en
          : workspace.workspace.name_es || workspace.workspace.name_en}
      </ListSubheader>,
      items
    ]
  }

  const handleCategoryChange = (event) => {
    let selectedValue = event.target.value

    let workspace = subcategories[parseInt(selectedValue / 1000)].workspace
    let subcategory =
      subcategories[parseInt(selectedValue / 1000)].subcategories[
        parseInt(selectedValue % 1000)
      ]

    setSelectedWorkspace(workspace.workspace_id)
    setSelectedSubcategory(subcategory.id)
    setSelectedFile(null)
  }

  const CategorySelect = () => {
    if (selectedCategoryName) {
      return (
        <FormControl fullWidth disabled>
          <InputLabel>Category</InputLabel>
          <Select value={-1} label='Category'>
            <MenuItem value={-1}>{selectedCategoryName}</MenuItem>
          </Select>
        </FormControl>
      )
    }

    // Assigning at workspace level
    const filtered =
      selectedWorkspaceId && !selectedSubcategoryId
        ? subcategories.filter(
            (s) => s.workspace.workspace_id === selectedWorkspaceId
          )
        : subcategories

    const selectedWorkspaceIdx = subcategories.findIndex(
      (s) => s.workspace.workspace_id === selectedWorkspace
    )
    const selectedSubcategoryIdx = subcategories[
      selectedWorkspaceIdx
    ]?.subcategories.findIndex((sub) => sub.id === selectedSubcategory)
    const selectedValue = selectedSubcategory
      ? selectedWorkspaceIdx * 1000 + selectedSubcategoryIdx
      : null

    return (
      <FormControl fullWidth>
        <InputLabel htmlFor='grouped-select'>Category</InputLabel>
        <Select
          id='grouped-select'
          label='Category'
          value={selectedValue}
          onChange={handleCategoryChange}
        >
          {filtered?.map((workspace, idx) => renderSelectGroup(workspace, idx))}
        </Select>
      </FormControl>
    )
  }

  const ClientSelect = () => {
    if (selectedClientOrCompanyName) {
      return (
        <FormControl fullWidth disabled>
          <InputLabel>Client or Company</InputLabel>
          <Select value={-1} label='Client or company'>
            <MenuItem value={-1}>{selectedClientOrCompanyName}</MenuItem>
          </Select>
        </FormControl>
      )
    }

    return (
      <FormControl fullWidth>
        <InputLabel htmlFor='grouped-select-client'>
          {'Client or company'}
        </InputLabel>
        <Select
          value={selectedClientOrCompany}
          id='grouped-select-client'
          label='Client or company'
          onChange={(event) => {
            setSelectedClientOrCompany(event.target.value)
          }}
        >
          <ListSubheader>Client</ListSubheader>
          {clientOrCompanies
            .filter((c) => c.type === 'client')
            .map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.name}
              </MenuItem>
            ))}
          <ListSubheader>Companies</ListSubheader>
          {clientOrCompanies
            .filter((c) => c.type !== 'client')
            .map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    )
  }

  const FileSelect = () => {
    if (selectedFileName) {
      return (
        <FormControl fullWidth disabled>
          <InputLabel>Custom Directory</InputLabel>
          <Select value={-1} label='Custom Directory'>
            <MenuItem value={-1}>{selectedFileName}</MenuItem>
          </Select>
        </FormControl>
      )
    }

    if (availableCustomDirs.length === 0) return null

    return (
      <FormControl fullWidth>
        <InputLabel>Custom Directory (optional)</InputLabel>
        <Select
          value={selectedFile ?? ''}
          label='Custom Directory (optional)'
          onChange={(e) => {
            setSelectedFile(e.target.value || null)
          }}
        >
          <MenuItem value=''>
            <em>None</em>
          </MenuItem>
          {availableCustomDirs.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Step 1: Select Documents</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <ClientSelect />
          <CategorySelect />
          <FileSelect />

          <input
            type='file'
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />

          <Box
            onClick={() => fileInputRef.current.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            sx={{
              border: '2px dashed',
              borderColor: files.length > 0 ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 6,
              textAlign: 'center',
              bgcolor: 'action.hover',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.selected' }
            }}
          >
            <CloudUploadIcon
              sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}
            />
            <Typography variant='h6'>Click or Drag Files</Typography>
            <Typography variant='caption' color='textSecondary'>
              Support for multiple documents
            </Typography>
          </Box>

          {files.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {files.map((f, i) => (
                <Chip
                  key={i}
                  label={f.name}
                  onDelete={() => setFiles(files.filter((_, idx) => idx !== i))}
                />
              ))}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color='inherit'>
          Cancel
        </Button>
        <Button
          variant='contained'
          disabled={
            !selectedClientOrCompany ||
            !selectedWorkspace ||
            !selectedSubcategory ||
            files.length === 0
          }
          onClick={() =>
            onContinue(
              files,
              selectedClientOrCompany,
              selectedWorkspace,
              selectedSubcategory,
              selectedFile
            )
          }
        >
          Continue ({files.length} Files)
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignDocumentsDialog
